import React from 'react';
import { Calendar as CalendarIcon, AlertCircle } from 'lucide-react';

/**
 * DatePicker Component
 * 
 * Date input with calendar icon and consistent styling.
 * 
 * @example
 * ```tsx
 * <DatePicker
 *   label="Transaction Date"
 *   value={date}
 *   onChange={(e) => setDate(e.target.value)}
 * />
 * ```
 */

export interface DatePickerProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  /** Label text */
  label?: string;
  /** Helper text */
  helperText?: string;
  /** Error message */
  error?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Full width */
  fullWidth?: boolean;
  /** Container className */
  containerClassName?: string;
}

const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      label,
      helperText,
      error,
      size = 'md',
      fullWidth = false,
      containerClassName = '',
      className = '',
      id,
      disabled,
      required,
      ...props
    },
    ref
  ) => {
    const inputId = id || `datepicker-${React.useId()}`;

    // Base styles
    const baseStyles = [
      'bg-white border rounded-lg',
      'text-gray-900',
      'transition-all duration-200',
      'focus:outline-none focus:ring-2',
      'disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60',
      'dark:bg-dark-card dark:text-dark-text',
      'dark:disabled:bg-dark-surface',
      'cursor-pointer',
    ].join(' ');

    // State styles
    const stateStyles = error
      ? 'border-error-500 focus:border-error-500 focus:ring-error-500/20'
      : 'border-gray-200 focus:border-primary-500 focus:ring-primary-500/20 dark:border-dark-border';

    // Size styles with icon space - MUCH MORE padding to prevent icon overlap
    const sizeStyles = {
      sm: 'pl-12 pr-3 py-1.5 text-sm',   // Increased to pl-12 (48px)
      md: 'pl-14 pr-3 py-2.5 text-base', // Increased to pl-14 (56px)
      lg: 'pl-16 pr-4 py-3 text-lg',     // Increased to pl-16 (64px)
    };

    // Icon wrapper width to reserve space on the left consistently
    const iconWrapperWidth = {
      sm: 'w-10',  // 40px reserved area
      md: 'w-12',  // 48px reserved area
      lg: 'w-14',  // 56px reserved area
    };

    const widthStyles = fullWidth ? 'w-full' : '';
    const containerWidth = fullWidth ? 'w-full' : '';

    const inputStyles = [
      baseStyles,
      stateStyles,
      sizeStyles[size],
      widthStyles,
      className,
    ].join(' ');

    return (
      <div className={`${containerWidth} ${containerClassName}`}>
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-gray-900 mb-1.5 dark:text-dark-text"
          >
            {label}
            {required && <span className="text-error-500 ml-1">*</span>}
          </label>
        )}

        {/* Input Container */}
        <div className="relative">
          {/* Calendar Icon - fixed-width left slot ensures consistent spacing */}
          <div
            className={`absolute inset-y-0 left-0 ${iconWrapperWidth[size]} text-gray-700 dark:text-gray-300 pointer-events-none z-10 flex items-center justify-center`}
          >
            <CalendarIcon className="w-5 h-5" strokeWidth={2.5} />
          </div>

          {/* Date Input */}
          <input
            ref={ref}
            type="date"
            id={inputId}
            disabled={disabled}
            required={required}
            className={inputStyles}
            style={{
              paddingLeft: size === 'sm' ? '44px' : size === 'lg' ? '60px' : '52px'
            }}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={
              error
                ? `${inputId}-error`
                : helperText
                ? `${inputId}-helper`
                : undefined
            }
            {...props}
          />

          {/* Error Icon */}
          {error && (
            <div
              className={`absolute right-3 top-1/2 -translate-y-1/2 text-error-500`}
            >
              <AlertCircle size={size === 'sm' ? 16 : size === 'lg' ? 24 : 20} />
            </div>
          )}
        </div>

        {/* Helper Text or Error */}
        {(helperText || error) && (
          <p
            id={error ? `${inputId}-error` : `${inputId}-helper`}
            className={`mt-1.5 text-sm ${
              error
                ? 'text-error-600 dark:text-error-400'
                : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

DatePicker.displayName = 'DatePicker';

export default DatePicker;
